/**
 * Has this trip's pickup point moved since the Rider last looked?
 *
 * When a store changes its dispatch location, trips whose pickup has not started
 * are re-prepared from the new place. The Rider's screen refreshes on its own,
 * but the only message it used to show was "Your route was updated. The latest
 * stop order is shown below" - which describes the most important change a Rider
 * can receive, "you are now collecting somewhere else", as a reordering of stops.
 * A Rider already navigating in Google Maps would have no other way to learn it.
 *
 * Only a change of the physical point counts. A reworded address at the same pin
 * sends nobody anywhere different, and announcing it would train Riders to ignore
 * the notice that matters.
 */

const DECIMALS = 6; // about ten centimetres - finer than any real pickup move

function point(origin) {
  const latitude = Number(origin?.latitude);
  const longitude = Number(origin?.longitude);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
  return `${latitude.toFixed(DECIMALS)},${longitude.toFixed(DECIMALS)}`;
}

/**
 * @returns {{ address: string|null } | null} the new pickup when it moved, else null
 */
export function pickupMovedBetween(previous, next) {
  // Only the same trip can have moved. A new trip is not a change.
  if (!previous || !next || previous.id !== next.id) return null;
  // Once pickup has started the origin is frozen; nothing to announce.
  if (next.pickupStartedAt) return null;
  const before = point(previous.pickupOrigin);
  const after = point(next.pickupOrigin);
  if (!before || !after || before === after) return null;
  return { address: next.pickupOrigin?.address || null };
}

export function pickupMovedNotice(moved) {
  if (!moved) return null;
  return moved.address
    ? `The store moved its pickup point. Collect from ${moved.address} instead.`
    : 'The store moved its pickup point. Check the new location on the map before you set off.';
}
